/**
 * Reset step 3 — the new password, with the policy shown as a live checklist before the user ever
 * submits (it used to surface only as the list of what had already failed): the ONE checklist,
 * `@puffin-core/auth-shell`'s, over the ONE `@puffin-core/password-policy` and the flow's context
 * (L-R7CLOSE row 39 / row 42). Show / hide on both
 * fields (NIST SP 800-63B §5.1.1.2: offer to display the secret so the user can check what they
 * typed). One toggle drives both fields, so the confirmation never hides what the new field shows:
 * auth-shell's ONE `<PasswordField>` (PALM-PHONE-UAT-001 A6), controlled by the flow's `showPassword`
 * on the new field and mirrored (`toggle={false}`) on the confirmation.
 *
 * @module @puffin-core/login-shell/components/passwordReset/ResetNewPasswordStep
 */

import { useEffect, useRef } from 'react';
import PropTypes from 'prop-types';
import { PasswordField, PasswordRequirementList } from '@puffin-core/auth-shell';
import { ResetErrorAlert } from './ResetErrorAlert.jsx';
import { ResetStepIndicator } from './ResetStepIndicator.jsx';

const REQUIREMENTS_ID = 'reset-new-password-requirements';

/**
 * @param {Object} props
 * @param {Object} props.flow - `usePasswordResetFlow`'s return
 * @param {Function} props.translate
 * @returns {React.JSX.Element}
 */
export function ResetNewPasswordStep({ flow, translate }) {
  const { state } = flow;
  const passwordRef = useRef(null);
  useEffect(() => {
    passwordRef.current.focus();
  }, []);

  return (
    <form
      onSubmit={flow.handleCompleteReset}
      aria-label={translate('modal.passwordReset.form.newPassword')}
      noValidate
    >
      <ResetStepIndicator current={3} total={3} translate={translate} />
      <p className="text text-sm text-muted mb-4">
        {translate('modal.passwordReset.multiStep.newPasswordBody')}
      </p>
      <ResetErrorAlert error={state.error} translate={translate} />
      {/* PALM-PHONE-UAT-001 A6: the ONE password field, controlled by the flow's own `showPassword`;
          the confirmation MIRRORS it (`toggle={false}`), so it can never hide what the new field shows. */}
      <PasswordField
        id="reset-new-password"
        label={translate('modal.passwordReset.multiStep.newPasswordLabel')}
        value={state.newPassword}
        onChange={(e) => flow.setField('newPassword', e.target.value)}
        autoComplete="new-password"
        disabled={state.isLoading}
        shown={state.showPassword}
        onToggleShown={flow.toggleShowPassword}
        describedBy={REQUIREMENTS_ID}
        inputRef={passwordRef}
        inputProps={{ 'aria-required': 'true' }}
        testId="reset-new-password-input"
        toggleTestId="reset-password-toggle"
      />
      <div id={REQUIREMENTS_ID} className="form-group">
        <PasswordRequirementList
          password={state.newPassword}
          context={flow.passwordContext}
          testId="reset-password-requirements"
        />
      </div>
      <PasswordField
        id="reset-confirm-password"
        label={translate('modal.passwordReset.multiStep.confirmPasswordLabel')}
        value={state.confirmPassword}
        onChange={(e) => flow.setField('confirmPassword', e.target.value)}
        autoComplete="new-password"
        disabled={state.isLoading}
        toggle={false}
        shown={state.showPassword}
        inputProps={{ 'aria-required': 'true' }}
        testId="reset-confirm-password-input"
      />
      <button
        type="submit"
        className="btn btn-primary btn-lg w-full"
        disabled={!state.newPassword || !state.confirmPassword || state.isLoading}
        aria-busy={state.isLoading ? 'true' : 'false'}
        data-testid="reset-new-password-submit"
      >
        {state.isLoading
          ? translate('modal.passwordReset.submit.loading')
          : translate('modal.passwordReset.multiStep.newPasswordSubmit')}
      </button>
    </form>
  );
}

ResetNewPasswordStep.propTypes = {
  flow: PropTypes.object.isRequired,
  translate: PropTypes.func.isRequired,
};

export default ResetNewPasswordStep;
